import * as React from 'react';
import { Box, SimpleGrid } from '@chakra-ui/react';
import { HStack, Heading, Text, VStack } from '@ayble-health/dashboard-components';
import {
  LaneBlockCard,
  type LaneBlockCardProps,
  type NotesVariant,
} from '../PatientProfile/LaneBlockCard';
import { Callout } from './Callout';
import { Specimen } from './Specimen';
import { ChangeBadge } from './ChangeBadge';

/**
 * The block card — rendered from the real component, not a copy of it.
 *
 * This section used to hand-build the six states out of Box and Text to match
 * the Figma. It drifted within a day: the title came out semibold where the
 * card draws it regular, the assigner and progress rows came out at 14px where
 * the card uses 12, and every string was missing the 0.01em tracking careOS
 * puts on body text. Nobody had changed anything — the copy was simply never
 * the same object as the thing it documented.
 *
 * So it imports `LaneBlockCard` and drives it through its real props. The rest
 * of this audit already works that way (`FeatureAudit` renders the actual
 * `OverviewCard` and `StatusPill`), and the Prototype tab's whole claim is that
 * it is rendered from the live component library rather than hand-copied. A
 * second implementation of the densest composite in the product was the one
 * place that claim was not true.
 *
 * The careOS type and colour variables only resolve under `.careos-root`, so
 * every specimen here is wrapped in one — the same ancestor the patient profile
 * provides.
 */

/** The card fills its lane, so specimens need a lane-width column to sit in. */
const LANE_WIDTH = '300px';

const Frame = ({
  label,
  note,
  children,
}: {
  label: string;
  note?: string;
  children: React.ReactNode;
}) => (
  <VStack gap={2} align="stretch">
    <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
      {label}
    </Text>
    <Box className="careos-root" maxW={LANE_WIDTH}>
      {children}
    </Box>
    {note && (
      <Text my={0} fontSize="2xs" color="muted.600">
        {note}
      </Text>
    )}
  </VStack>
);

// ── The six status states ───────────────────────────────────────────────────

/**
 * Props lifted from the Care Guide's own lane data, so a state shown here is a
 * state the board can actually produce. Divergence between this list and
 * `CARE_PLAN_LANES` means one of the two is lying.
 */
const STATES: Array<[string, LaneBlockCardProps, string]> = [
  [
    'suggested',
    {
      variant: 'suggested',
      title: 'Food Challenge',
      assignedBy: 'Suggested by Ayble',
      statCallout: (
        <>
          <strong>Why?</strong> This interventions helps 78% of people like Sandy
          with bloating.
        </>
      ),
      onSetup: () => {},
      onDismiss: () => {},
    },
    'Bucket 3 — own anatomy (two actions + rationale callout). Not a variant.',
  ],
  [
    'in-progress',
    {
      variant: 'in-progress',
      title: 'Elimination Challenge',
      assignedBy: 'J. Brown, RD',
      engagement: {
        lastEngaged: 'Today, 9:12 AM ET',
        count: 14,
        total: 21,
        countNoun: 'Gut Checks',
      },
    },
    'The base card. No header strip.',
  ],
  [
    'overdue',
    {
      variant: 'overdue',
      title: 'Stay Hydrated',
      assignedBy: 'E. Rayas, Coach',
      headerLabel: '14 days ago',
      engagement: {
        lastEngaged: '14 days ago',
        count: 0,
        isRecent: false,
        streakEndedDays: 8,
      },
      // Over 30 days, so the audit shows the dated form ("Added 09/12/2026").
      notes: { variant: 'trouble-implementing', count: 3, extraCount: 2, lastAddedOn: '2026-09-12', asOf: '2026-10-26', symptoms: 'no-change' },
    },
    'Bucket 2 — header tone + action label. Body anatomy unchanged.',
  ],
  [
    'error',
    {
      variant: 'error',
      title: 'Send Lab Order to Labcorp',
      assignedBy: 'T. Haywood, MD',
      headerLabel: 'Error',
    },
    'Bucket 2 — same shape, different tone.',
  ],
  [
    'completed',
    {
      variant: 'completed',
      title: 'Complete New Lab Work',
      assignedBy: 'T. Haywood, MD',
      headerLabel: 'Completed',
      showViewResults: true,
      notes: { variant: 'out-of-range', count: 2 },
    },
    'Bucket 2 — plus the View Results affordance.',
  ],
  [
    'archived',
    {
      variant: 'archived',
      title: 'Low-FODMAP Reintroduction',
      assignedBy: 'J. Brown, RD',
    },
    'Bucket 2 — trailing chip instead of a header.',
  ],
];

// ── Addons ──────────────────────────────────────────────────────────────────

/**
 * The three addons are what make the card dense, and each is independently
 * optional — which is why they are shown on real cards rather than as loose
 * rows. A row lifted out of the card is exactly the copy this section stopped
 * making.
 */
const ENGAGEMENT_MODES: Array<[string, LaneBlockCardProps, string]> = [
  [
    'engagement · rolling window',
    {
      variant: 'in-progress',
      title: 'Mind-Gut Masterclass',
      assignedBy: 'GDH',
      engagement: { lastEngaged: 'Yesterday, 6:55 PM ET', count: 12 },
    },
    'Recency + frequency. Seafoam dot: engaged inside the window.',
  ],
  [
    'engagement · of-total',
    {
      variant: 'in-progress',
      title: 'Elimination Challenge',
      assignedBy: 'J. Brown, RD',
      engagement: {
        lastEngaged: 'Today, 9:12 AM ET',
        count: 14,
        total: 21,
        countNoun: 'Gut Checks',
      },
    },
    'Row 2 switches to "n of m" when total is set.',
  ],
  [
    'engagement · streak lapsed',
    {
      variant: 'in-progress',
      title: 'Stay Hydrated',
      assignedBy: 'E. Rayas, Coach',
      engagement: {
        lastEngaged: '14 days ago',
        count: 0,
        isRecent: false,
        streakEndedDays: 8,
      },
    },
    'Fuschia dot, and Row 2 is replaced by the streak warning.',
  ],
  [
    'progress · due date',
    {
      variant: 'in-progress',
      title: 'Modify Medication',
      assignedBy: 'T. Haywood, MD',
      progress: { dueLabel: 'Jan 7' },
    },
    'The progress addon is a date, not a metric.',
  ],
];

const NOTES_VARIANTS: Array<[NotesVariant, string]> = [
  ['stacked', 'Count only.'],
  ['trouble-implementing', 'Member-reported, with an overflow counter.'],
  ['out-of-range', 'Attachments, result outside the reference range.'],
  ['in-range', 'Attachments, result within range.'],
  ['no-change', 'Attachments, measured with no movement.'],
];

// ── The section ─────────────────────────────────────────────────────────────

export const BlockCardAudit = () => (
  <VStack gap={4} align="stretch">
    <HStack gap={2} align="center" wrap="wrap">
      <Heading size="md">Block card — the Care Guide lane card</Heading>
      <ChangeBadge kind="new" date="Aug 24, 2026" />
    </HStack>

    <Text fontSize="xs" color="muted.400">
      Every specimen below is the real <code>LaneBlockCard</code> from{' '}
      <code>PatientProfile/</code>, driven through its own props and wrapped in{' '}
      <code>.careos-root</code> so its type and colour variables resolve. Nothing
      here is redrawn, so nothing here can disagree with the board.
    </Text>

    <Callout title="This section was a copy, and the copy had already drifted">
      The six states used to be hand-built here out of <code>Box</code> and{' '}
      <code>Text</code> to match the Figma. Within a day the copy was wrong in
      four ways nobody had touched: the title rendered semibold where the card
      draws it regular, the assigner and the progress rows rendered at 14px where
      the card uses 12, and every string was missing the <code>0.01em</code>{' '}
      tracking careOS puts on body text.
      <br />
      <br />
      That is the whole argument for rendering the real component rather than a
      likeness of it. A documentation copy has no mechanism that keeps it honest
      — it looks right on the day it is written and is wrong the first time
      either side moves. Rendering the component means a drift is impossible
      rather than merely unlikely.
    </Callout>

    <Specimen
      name="Status states"
      note="One skeleton, six states. The header strip carries the status; the body anatomy is identical across all of them except suggested, which adds its own actions and callout."
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={5} w="100%">
        {STATES.map(([label, props, note]) => (
          <Frame key={label} label={label} note={note}>
            <LaneBlockCard {...props} />
          </Frame>
        ))}
      </SimpleGrid>
    </Specimen>

    <Specimen
      name="Addons — engagement and progress"
      note="Both sit below the title and are independently optional. Engagement has three modes and the mode is inferred from which props are set, not chosen by a flag."
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap={5} w="100%">
        {ENGAGEMENT_MODES.map(([label, props, note]) => (
          <Frame key={label} label={label} note={note}>
            <LaneBlockCard {...props} />
          </Frame>
        ))}
      </SimpleGrid>
    </Specimen>

    <Specimen
      name="Addon — notes and attachments"
      note="Five variants on one footer row. The noun switches between Notes and Attachments with the variant, and the result chip is Chip — every value it needs already exists as a variant."
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={5} w="100%">
        {NOTES_VARIANTS.map(([variant, note]) => (
          <Frame key={variant} label={`notes · ${variant}`} note={note}>
            <LaneBlockCard
              variant="in-progress"
              title="Complete New Lab Work"
              assignedBy="T. Haywood, MD"
              notes={{
                variant,
                count: 2,
                ...(variant === 'trouble-implementing' ? { extraCount: 2 } : {}),
              }}
            />
          </Frame>
        ))}
      </SimpleGrid>
    </Specimen>

    <Callout title="Production ships one of these six, and it does not look like this">
      The lane board exists in production (
      <code>overview/LaneBlockCard.tsx</code>) but renders a title,{' '}
      <code>Assigned By</code>, a raw <code>2026-06-24</code> date, and a plain
      metric line. No status headers, no addons, no result chips, no kebab.
      <br />
      <br />
      <strong>What to change, in the order that matters.</strong> The status
      header carries the most information per pixel — it is the only thing that
      tells a coach which card needs them without reading the body — so it goes
      first. Then the addons, which replace the raw date line. Then the result
      chips, which are pure adoption: every value already exists as a{' '}
      <code>Chip</code> variant.
      <br />
      <br />
      <strong>Dates are the quiet one.</strong> Production prints{' '}
      <code>2026-06-24</code>. The prototype says <em>Yesterday, 6:55 PM ET</em>,{' '}
      <em>14 days ago</em>, <em>Jan 7</em> — relative where recency is the point,
      short-form where it is a fact. A coach scanning a lane cannot do date
      arithmetic.
    </Callout>
  </VStack>
);
